// Shared media utilities for staff/student images and avatars
// Kept intentionally simple; behavior aligned with TopBar components and backend images routes.

export function normalizeImagePath(imagePath) {
  if (!imagePath) return '';
  let path = String(imagePath).replace(/\\/g, '/');
  // Strip known prefixes like "staff_images/" or "/storage/staff_images/"
  path = path.replace(/^\/storage\//, '');
  path = path.replace(/^staff_images\//i, '');
  path = path.replace(/^student_images\//i, '');
  return path;
}

// Fetch a staff image as a blob URL. Fallback to returning a URL path if fetch fails.
export async function fetchStaffImageBlob(filename, { mediaBaseUrl, headerName, headerValue } = {}) {
  if (!filename) return null;
  const cleanName = normalizeImagePath(filename);

  // Prefer media service base URL if provided; otherwise, hit backend images route.
  const url = mediaBaseUrl
    ? `${mediaBaseUrl.replace(/\/$/, '')}/staff/${encodeURIComponent(cleanName)}`
    : `/api/staff/${encodeURIComponent(cleanName)}`;

  const headers = {};
  if (headerName && headerValue) {
    headers[headerName] = headerValue;
  }

  try {
    const resp = await fetch(url, { headers, credentials: 'include' });
    if (!resp.ok) {
      return null;
    }
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  } catch {
    // On any error, just return null so caller can fall back.
    return null;
  }
}

// Fetch a student image URL. For this SPA we simply return a stable backend URL
// under the Express images router (/api/students or /api/student_images).
// The signature mirrors fetchStaffImageBlob so existing callers can pass
// optional media configuration, but it's not required here.
export async function fetchStudentImageBlob(filename, _config = {}) {
  if (!filename) return null;
  const cleanName = normalizeImagePath(filename);
  // Prefer the /student_images path which is registered on the images router.
  return `/api/student_images/${encodeURIComponent(cleanName)}`;
}

// Simple initials-based avatar generator (used as a last resort fallback)
// Returns a small inline SVG data URL that can be used directly in <img src>.
export function getInitialsAvatar(name) {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64">
      <rect width="100%" height="100%" fill="#e5e7eb" />
      <text x="50%" y="50%" dominant-baseline="central" text-anchor="middle"
            fill="#374151" font-family="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
            font-size="24" font-weight="600">
        ${initials}
      </text>
    </svg>
  `;

  const encoded = encodeURIComponent(svg.trim())
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');

  return `data:image/svg+xml,${encoded}`;
}

// Default avatar descriptor by gender; caller decides how to render it (icon/color/etc.)
export function getDefaultAvatar(gender) {
  const g = String(gender || '').toLowerCase();
  if (g.startsWith('f')) {
    return { type: 'default', variant: 'female' };
  }
  if (g.startsWith('m')) {
    return { type: 'default', variant: 'male' };
  }
  return { type: 'default', variant: 'neutral' };
}
